import React from "react";
import { injectIntl, FormattedMessage } from "react-intl";
import { connect } from "react-redux";
import CFPSCropper from "./CFPSCropper";
import SmartComponent from "../SmartComponent";
import { calculateImageHeight } from "../Panel";
import { updateCrop, updateZoom } from "../../actions/panel";
import { getColourizedImageUrl } from "../../selectors";
import { colourizedImageSetId } from "../../reducers/image";

const defaultCrop = { x: 0, y: 0 };
const defaultZoom = 1;

export class PanelImage extends SmartComponent {
  constructor(props) {
    super(props);
    this.updateCropIfNecessary = this.updateCropIfNecessary.bind(this);
    this.updateCanvas = this.updateCanvas.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);

    // Store a canvas with the greyscale image so we can lookup temperature even if color mapping has been applied.
    this.greyCanvas = document.createElement("canvas");
    this.updateGreyCanvas();

    this.state = {
      imageDownloadTime: null,
      tempImageUrl: null,
      hoveredTemperature: null, // The greyscale value of the pixel the user is hovering over
      lastCoordinates: null
    };

    this.cropperRef = React.createRef();
  }

  updateGreyCanvas() {
    if (!this.props.greyScaleUrl) {
      // There is no image to draw yet.
      return null;
    }

    const img = document.createElement("img");
    img.onload = () => {
      this.greyCanvas.width = img.naturalWidth;
      this.greyCanvas.height = img.naturalHeight;
      this.greyCanvas.getContext("2d").drawImage(img, 0, 0);
      if (this.state.lastCoordinates !== null) {
        const [x, y] = this.state.lastCoordinates;
        const temperature = this.getTemperatureAt(x, y);
        this.setState({ hoveredTemperature: temperature });
      }
    };
    img.setAttribute("crossOrigin", "");
    img.setAttribute("src", this.props.greyScaleUrl);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.greyScaleUrl !== this.props.greyScaleUrl) {
      // If the image url has changed, update the greyscale canvas
      const img = document.createElement("img");
      img.onload = () => {
        this.greyCanvas.width = img.naturalWidth;
        this.greyCanvas.height = img.naturalHeight;
        this.greyCanvas.getContext("2d").drawImage(img, 0, 0);
        if (this.state.lastCoordinates !== null) {
          const [x, y] = this.state.lastCoordinates;
          const temperature = this.getTemperatureAt(x, y);
          this.setState({ hoveredTemperature: temperature });
        }
      };
      img.setAttribute("crossOrigin", "");
      img.setAttribute("src", this.props.greyScaleUrl);
    }
  }

  onMouseMove(event) {
    // Based on which event triggered this, we know which element should be the image
    const container = event.currentTarget.children[0];
    const img = container.children[0];
    // If we're hovering over the zoom buttons or the temperature display overlay itself, or if we
    // don't actually have a loaded image, ignore
    if (this.scale === null || img.nodeName !== "IMG" || !img.complete) {
      return;
    }

    const boundingRect = container.getBoundingClientRect();
    let relativeX = this.getRelativeCoordinate(
      event.pageX,
      boundingRect.left,
      img.width,
      this.props.zoom,
      this.props.crop.x,
      img.naturalWidth
    );
    let relativeY = this.getRelativeCoordinate(
      event.pageY,
      boundingRect.top,
      img.height,
      this.props.zoom,
      this.props.crop.y,
      img.naturalHeight
    );

    // Because of the way the mouse event is triggered, it's possible the user is hovering outside
    // of the actual image; catch this edge case, and skip updating the greyscale value
    if (
      relativeY < 0 ||
      relativeY > img.naturalHeight ||
      relativeX < 0 ||
      relativeX > img.naturalWidth
    ) {
      return;
    } else {
      const temp = this.getTemperatureAt(relativeX, relativeY);
      this.setState({
        hoveredTemperature: temp,
        lastCoordinates: [relativeX, relativeY]
      });
    }
  }

  getTemperatureAt(x, y) {
    // Use the relative coordinates to get the image data from the canvas
    const imageData = this.greyCanvas.getContext("2d").getImageData(x, y, 1, 1);

    // The imageData is an array in the format [r, g, b, a]
    const [r, g, b] = imageData.data;
    const hoveredGreyscale = Math.round((r + g + b) / 3);

    return this.props.temperatureDisplayScale[hoveredGreyscale];
  }

  getRelativeCoordinate(
    mouseCoordinate,
    imageOffset,
    imageDimension,
    zoom,
    cropValue,
    naturalImageDimension
  ) {
    // This is a function that returns a calculated coordinate value (either x or y, depending on
    // what it's given), relative to the original image, regardless of the size of the panel it's
    // being displayed in or the zoom and crop settings

    // Get coordinate relative to the image
    let relativeCoordinate = mouseCoordinate - imageOffset;

    // We need to now translate those based on the panel's zoom and crop

    let unscaledWidth = imageDimension * zoom; // Get the image's total dimension if we weren't cropping it
    let missingWidth = unscaledWidth - imageDimension; // Figure out how much of it we _are_ cropping

    // By default, assume it's split in half (because if the origin is at 0, 0, it will be)
    let translateValue = missingWidth / 2;

    // Subtract that missing half-width from the crop, and take the absolute value
    // * Example 1:
    //    * cropValue = 0 (middle of image)
    //    * translateValue = 10 (there are a total of 20 pixels that the user can't see)
    //    * new translateValue = 10
    // * Example 2:
    //    * cropValue = 10 (panned to the far left)
    //    * translateValue = 10 (there are a total of 20 pixels that the user can't see)
    //    * new translateValue = 0
    // * Example 1:
    //    * cropValue = -10 (panned to far right)
    //    * translateValue = 10 (there are a total of 20 pixels that the user can't see)
    //    * new translateValue = 20
    translateValue = Math.abs(cropValue - translateValue);

    relativeCoordinate = relativeCoordinate + translateValue;

    // These are now relative to the _resized_ image based on the panel size, whatever that is; need to convert it
    relativeCoordinate =
      relativeCoordinate * (naturalImageDimension / imageDimension);

    // If we are zoomed in, we need to adjust accordingly
    relativeCoordinate = relativeCoordinate / zoom;
    return relativeCoordinate;
  }

  onZoom(amount, event) {
    // Store whether or not this event is a dblclick event
    const isDblClick = event ? event.type === "dblclick" : false;
    // If we have a double click event on one of the zoom buttons
    if (
      event !== undefined &&
      isDblClick &&
      event.target.className.indexOf("zoom") >= 0
    ) {
      // We ignore, explicitly
      return;
    }
    let newZoom = this.props.zoom + amount;
    if (newZoom < 1) {
      newZoom = 1;
    } else if (newZoom > 3) {
      newZoom = 3;
    }

    this.props.updateZoom(newZoom);
    // If it's a double click event, we also need to crop
    if (isDblClick) {
      const x = event.nativeEvent.offsetX;
      const y = event.nativeEvent.offsetY;
      const panelWidth = event.currentTarget.clientWidth;
      const panelHeight = event.currentTarget.clientHeight;

      this.updateCropIfNecessary(
        this.getNewCrop(
          x,
          y,
          panelWidth,
          panelHeight,
          this.props.zoom,
          newZoom,
          this.props.crop
        )
      );
    }
  }

  updateCropIfNecessary(newCrop) {
    // This gets called frequently, including just when we've started displaying a new image because
    // the user advanced to a new frame.
    // Filter out the useless changes to crop
    if (
      !this.props.crop ||
      this.props.crop.x !== newCrop.x ||
      this.props.crop.y !== newCrop.y
    ) {
      this.props.updateCrop(newCrop);
    }

    if (
      this.props.temperatureDisplayScale &&
      this.props.temperatureDisplayEnabled
    ) {
      this.updateCanvas();
    }
  }

  updateCanvas() {
    // Set the same dimensions as the _unaltered image_
    const img = this.cropperRef.current.containerRef.children[0];
    this.canvas.width = img.naturalWidth;
    this.canvas.height = img.naturalHeight;

    // Draw image data onto the temporary canvas
    const drawingContext = this.canvas.getContext("2d");
    try {
      drawingContext.drawImage(img, 0, 0);
      this.updateGreyCanvas();
    } catch (err) {
      console.error(err);
      // This is not particularly good, but let's not let the whole application crash
      return;
    }
  }

  getNewCrop(x, y, panelWidth, panelHeight, oldZoom, newZoom, currentCrop) {
    // The event gives us the offset from the top-left corner of the image, where 0, 0 would be
    // the top-left corner, and {width}, {height} would be the bottom right.
    //
    // The react-easy-crop library seems to use the following numbers for "crop"
    // coordinates:
    //     X  Y
    //   * 0, 0: center of the image
    //   * +, +: Top _left_ corner of image
    //   * -, +: Top _right corner of image
    //   * -, -: Bottom _right_ corner of image
    //   * +, -: Bottom _left_ corner of image
    //
    // Thus, we have to transform them:

    // Convert x into a delta:
    let deltaX = x - panelWidth / 2; // Change to be based on a 0, 0 origin in the centre
    deltaX = -deltaX; // Convert to the sign that `react-easy-crop` expects
    deltaX = deltaX / oldZoom; // Coordinates for fully zoomed out
    deltaX = deltaX * newZoom; // Coordinates for new zoom level

    // Determine what the coordinates _would_ be if we just zoomed in without any further adjustments
    let oldCropX = currentCrop.x; // What we're currently centered on
    oldCropX = oldCropX / oldZoom; // Coordinates for fully zoomed out
    oldCropX = oldCropX * newZoom; // New zoom level

    let newX = oldCropX + deltaX;

    let deltaY = y - panelHeight / 2;
    deltaY = -deltaY;
    deltaY = deltaY / oldZoom;
    deltaY = deltaY * newZoom;

    let oldCropY = currentCrop.y;
    oldCropY = oldCropY / oldZoom;
    oldCropY = oldCropY * newZoom;

    let newY = oldCropY + deltaY;

    // Prevent against maximum
    let maxXAllowed = panelWidth * newZoom; // Furthest right a valid image-related point is
    maxXAllowed = maxXAllowed / 2; // Split it in half, since we orient using a centre 0,0 origin
    maxXAllowed = maxXAllowed - panelWidth / 2; // Shove it over half of the viewport, since the crop point is at the centre

    let maxYAllowed = panelHeight * newZoom;
    maxYAllowed = maxYAllowed / 2;
    maxYAllowed = maxYAllowed - panelHeight / 2;

    // If we have gone over that in one way or another
    if (Math.abs(newX) > maxXAllowed) {
      // Set it to the maximum, matching the sign
      newX = newX < 0 ? -maxXAllowed : maxXAllowed;
    }

    if (Math.abs(newY) > maxYAllowed) {
      newY = newY < 0 ? -maxYAllowed : maxYAllowed;
    }

    return {
      x: newX,
      y: newY
    };
  }

  render() {
    let mouseMoveEventHandler = null;
    // If this panel should have the ability to display the temperature hover
    if (this.props.temperatureDisplayScale) {
      // We use this for when we're looking up the greyscale value for IR temperature. This prevents
      // us from re-creating a canvas every time
      this.canvas = this.canvas || document.createElement("canvas");

      if (this.props.temperatureDisplayEnabled) {
        mouseMoveEventHandler = this.onMouseMove;
      }
    }

    let img;

    // If we have a frameList to display
    if (this.props.hasFrameList) {
      img = [
        <CFPSCropper
          key="cropper"
          image={this.props.imgUrl}
          crop={this.props.crop || defaultCrop}
          zoom={this.props.zoom || defaultZoom}
          onCropChange={this.updateCropIfNecessary}
          onZoomChange={this.props.updateZoom}
          aspect={this.props.panelRatio}
          showGrid={false}
          panelWidth={this.props.panelWidth}
          panelPosition={this.props.position}
          onImgLoad={this.props.onImgLoad}
          ref={this.cropperRef}
          style={{
            containerStyle: {
              cursor: this.props.temperatureDisplayEnabled ? "default" : "move"
            }
          }}
        />,
        <a
          key="zoom-in"
          onClick={this.onZoom.bind(this, 0.5)}
          className="zoom zoom-in"
          title={this.props.intl.formatMessage({ id: "zoom-in" })}
        >
          +
        </a>,
        <a
          key="zoom-out"
          onClick={this.onZoom.bind(this, -0.5)}
          className="zoom zoom-out"
          title={this.props.intl.formatMessage({ id: "zoom-out" })}
        >
          -
        </a>
      ];
      if (
        this.props.temperatureDisplayEnabled &&
        this.state.hoveredTemperature !== null
      ) {
        img.push(
          <div key="temperature-overlay" className="temperature-overlay">
            {this.state.hoveredTemperature.toFixed(1) + "°"}
          </div>
        );
      }
    } else {
      img = (
        <div
          className="image-not-available"
          style={{
            height: calculateImageHeight(
              this.props.panelWidth,
              this.props.panelRatio
            )
          }}
        >
          <h2>
            <FormattedMessage id="no-image-available" />
          </h2>
        </div>
      );
    }

    return (
      <div
        onMouseMove={mouseMoveEventHandler}
        className={`panel-content${
          this.props.panelIsCurrent && this.props.isLiveMode ? " current" : ""
        }`}
        onDoubleClick={this.onZoom.bind(this, 0.5)}
      >
        {img}
      </div>
    );
  }
}

// For testing, export just the base component
export const PanelImageClass = injectIntl(PanelImage);

const mapStoreToProps = (state, props) => {
  let imgUrl;
  if (
    window.settings.workspace.canViewSatelliteColour &&
    props.colourCurve.selected
  ) {
    imgUrl = getColourizedImageUrl(
      colourizedImageSetId(props.panelId, props.colourCurve.selected),
      window.urls.image(props.activeImageId)
    )(state);
  } else {
    imgUrl = window.urls.image(props.activeImageId);
  }
  return {
    imgUrl,
    greyScaleUrl: window.urls.image(props.activeImageId)
  };
};

const mapDispatchToProps = {
  updateCrop,
  updateZoom
};

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...ownProps,
  ...stateProps,
  updateCrop: dispatchProps.updateCrop.bind(null, ownProps.panelId),
  updateZoom: dispatchProps.updateZoom.bind(null, ownProps.panelId)
});

export default injectIntl(
  connect(mapStoreToProps, mapDispatchToProps, mergeProps)(PanelImage)
);
